import React, { useState } from 'react';
import { ListGroup, Button, Spinner, Modal } from 'react-bootstrap';
import { Document } from '../../types/types';
import { candidateService } from '../../services/api/candidateService';
import DocumentViewerModal, { getViewerType } from './DocumentViewerModal';

interface DocumentListProps {
    documents: Document[];
    candidateId: string;
    onDelete?: (documentId: string) => Promise<void> | void;
    readOnly?: boolean;
}

const DocumentList: React.FC<DocumentListProps> = ({
    documents,
    candidateId,
    onDelete,
    readOnly = false
}) => {
    const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
    const [documentToView, setDocumentToView] = useState<Document | null>(null);

    const handleDownload = async (doc: Document) => {
        setLoading(prev => ({ ...prev, [doc.id]: true }));

        try {
            //const blob = await candidateService.downloadDocument(candidateId, doc.id);

            //// Create a URL for the blob
            //const url = window.URL.createObjectURL(blob);

            // Create a temporary link and trigger download
            const link = document.createElement('a');
            link.href = doc.blobUri;
            link.download = doc.fileName;
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(doc.blobUri);
        } catch (error) {
            console.error('Error downloading document:', error);
            alert('Failed to download the document. Please try again.');
        } finally {
            setLoading(prev => ({ ...prev, [doc.id]: false }));
        }
    };

    const confirmDelete = (document: Document) => {
        setDocumentToDelete(document);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!documentToDelete) return;

        setLoading(prev => ({ ...prev, [documentToDelete.id]: true }));

        try {
            // Let the parent handle deletion (and its store/state updates) when provided
            if (onDelete) {
                await onDelete(documentToDelete.id);
            } else {
                await candidateService.deleteDocument(candidateId, documentToDelete.id);
            }

            setShowDeleteModal(false);
            setDocumentToDelete(null);
        } catch (error) {
            console.error('Error deleting document:', error);
            alert('Failed to delete the document. Please try again.');
        } finally {
            setLoading(prev => ({ ...prev, [documentToDelete.id]: false }));
        }
    };

    const getFileIcon = (fileName: string): string => {
        const extension = fileName.split('.').pop()?.toLowerCase() || '';

        switch (extension) {
            case 'pdf':
                return 'bi-file-earmark-pdf';
            case 'doc':
            case 'docx':
                return 'bi-file-earmark-word';
            case 'txt':
                return 'bi-file-earmark-text';
            case 'rtf':
                return 'bi-file-earmark-richtext';
            default:
                return 'bi-file-earmark';
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatUploadDate = (date: Date) => {
        return new Date(date).toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (documents.length === 0) {
        return <p className="text-muted">No documents uploaded</p>;
    }

    return (
        <>
            <ListGroup variant="flush">
                {documents.map(document => (
                    <ListGroup.Item key={document.id} className="px-0 py-2">
                        <div className="d-flex align-items-center">
                            <div className="me-2 fs-4">
                                <i className={getFileIcon(document.fileName)}></i>
                            </div>
                            <div className="d-flex flex-column flex-grow-1 min-width-0">
                                <div className="text-truncate fw-semibold">{document.fileName}</div>
                                <small className="text-muted">
                                    {formatFileSize(document.fileSize)} - {formatUploadDate(document.createdAt)}
                                </small>
                            </div>
                            <div className="ms-2 d-flex">
                                {getViewerType(document.fileName) && (
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="me-1"
                                        title="View"
                                        onClick={() => setDocumentToView(document)}
                                    >
                                        <i className="bi bi-eye"></i>
                                    </Button>
                                )}

                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-1"
                                    onClick={() => handleDownload(document)}
                                    disabled={loading[document.id]}
                                >
                                    {loading[document.id] ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <i className="bi bi-download"></i>
                                    )}
                                </Button>

                                {!readOnly && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => confirmDelete(document)}
                                        disabled={loading[document.id]}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </Button>
                                )}
                            </div>
                        </div>
                    </ListGroup.Item>
                ))}
            </ListGroup>

            <DocumentViewerModal
                document={documentToView}
                candidateId={candidateId}
                onHide={() => setDocumentToView(null)}
            />

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-exclamation-triangle text-danger me-2"></i>
                        Delete Document
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete <strong>{documentToDelete?.fileName}</strong>?
                    <div className="alert alert-warning mt-3 mb-0">
                        <i className="bi bi-exclamation-circle me-2"></i>
                        This action cannot be undone.
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDelete}
                        disabled={loading[documentToDelete?.id || '']}
                    >
                        {loading[documentToDelete?.id || ''] ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-trash me-2"></i>
                                Delete
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default DocumentList;